import { useMemo, useEffect, useState } from "react";
import {
  useTable,
  useFilters,
  useSortBy,
  usePagination,
  useRowSelect,
} from "react-table";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import { formatDate } from "../../../helpers/transformDate";
import "react-dates/initialize";
import "react-dates/lib/css/_datepicker.css";
import { DateRangePicker } from "react-dates";
import MyDatePresets from "../../../helpers/MyDatePresets";
import moment from "moment";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import "bootstrap/dist/css/bootstrap.min.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass, faX } from "@fortawesome/free-solid-svg-icons";
import { ColumnFilter } from "./ColumnFilter";
import { MyCheckbox } from "./CheckBoxRowSelector";
import { COLUMNS } from "./Columns";

import {
  Table,
  Spinner,
  Container,
  Row,
  Col,
  Button,
  InputGroup,
  Form,
} from "react-bootstrap";

export const ReviewTable = () => {
  const [companies, getCompanies] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("NEW");
  const [vendor_name, setVendor_name] = useState("");
  const [selection, setSelection] = useState([]);
  const [createdBeforeDate, setCreatedBeforeDate] = useState(999999999999);
  const [createdAfterDate, setCreatedAfterDate] = useState(1);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [focusedInput, setFocusedInput] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [filtersReset, setFiltersReset] = useState(false);

  const axiosPrivate = useAxiosPrivate();

  const handleChange = (value) => {
    return value;
  };

  const setQueryDates = (startDate, endDate) => {
    setCreatedAfterDate(moment.utc(startDate).valueOf());
    setCreatedBeforeDate(moment.utc(endDate).valueOf());
  };

  const columns = useMemo(() => COLUMNS, []);

  const navigation = useNavigate();

  const handleRowClick = (row) => {
    navigation("/reviews/" + row.original.id);
  };

  const defaultColumn = useMemo(
    () => ({
      Filter: ColumnFilter,
    }),
    []
  );

  function SelectColumnFilter({
    column: { filterValue, setFilter, preFilteredRows, id },
  }) {
    const options = useMemo(() => {
      const options = new Set();
      preFilteredRows.forEach((row) => {
        options.add(row.values[id]);
      });
      return [...options.values()];
    }, [id, preFilteredRows]);

    return (
      <select
        value={filterValue}
        onChange={(e) => {
          setStatus(e.target.value || undefined);
        }}
      >
        <option value="NEW">NEW</option>
        <option value="APPROVED">APPROVED</option>
        <option value="REJECTED">REJECTED</option>
        <option value="DELETED">DELETED</option>
      </select>
    );
  }

  const getToggleAllPageRowsSelectedProps = (props = {}) => {
    const rows = page.map((row) => row.original);
    return {
      ...props,
      checked: rows.length > 0 && rows.every((row) => row.isSelected),
      onChange: (e) => {
        const checked = e.target.checked;
        rows.forEach((row) => row.toggleRowSelected(checked));
      },
    };
  };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page, // Only the rows on the current page
    prepareRow,
    selectedFlatRows,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data: reviews,
      defaultColumn,
      initialState: { pageIndex: 0, pageSize: 20 },
    },
    useFilters,
    useSortBy, // Place useSortBy before usePagination
    usePagination,
    useRowSelect,
    (hooks) => {
      hooks.visibleColumns.push((columns) => [
        {
          id: "selection",
          Header: ({ getToggleAllPageRowsSelectedProps }) => (
            <MyCheckbox {...getToggleAllPageRowsSelectedProps()} />
          ),
          Cell: ({ row }) => (
            <MyCheckbox {...row.getToggleRowSelectedProps()} />
          ),
        },
        ...columns,
      ]);
    }
  );

  useEffect(() => {
    setSelection(selectedFlatRows.map((row) => row.original.id));
  }, [setSelection, selectedFlatRows]);

  const getAllReviews = async () => {
    setLoading(false);
    let isMounted = true;
    const controller = new AbortController();
    try {
      const response = await axiosPrivate.get(
        `reviews?status=${status}&vendor_name=${vendor_name}&createdAfter=${createdAfterDate}&createdBefore=${createdBeforeDate}`,
        {
          signal: controller.signal,
        }
      );
      if (isMounted) {
        setReviews(response.data);
        setLoading(true);
      }
    } catch (err) {
      toast.error(err.message);
    }
  };

  const getAllCompanies = async () => {
    const controller = new AbortController();
    try {
      const response = await axiosPrivate.get("vendors", {
        signal: controller.signal,
      });
      getCompanies(response.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    getAllCompanies();
  }, []);

  useEffect(() => {
    getAllReviews();
    setFiltersReset(false);
  }, [filtersReset]);

  useEffect(() => {
    getAllReviews();
  }, []);

  const handleApprove = (e) => {
    e.stopPropagation();
    e.preventDefault();

    axiosPrivate({
      method: "POST",
      url: `bulkConfirm`,
      data: { idList: selection },
    })
      .then(function (res) {
        toast.success("Vse ocene uspešno potrjene!");
      })
      .catch(function (res) {
        toast.error(res.message);
      });
    getAllReviews();
  };
  const handleReject = (e) => {
    e.stopPropagation();
    e.preventDefault();

    axiosPrivate({
      method: "POST",
      url: `bulkDecline`,
      data: { idList: selection },
    })
      .then(function (res) {
        toast.success("Vse ocene uspešno zavrnjene!");
      })
      .catch(function (res) {
        toast.error(res.message);
      });
    getAllReviews();
  };

  const onChangeHandler = (text) => {
    let matches = [];
    if (text.length > 0) {
      matches = companies.filter((company) => {
        const regex = new RegExp(`${text}`, "gi");
        return company.name.match(regex);
      });
    }

    setSuggestions(matches);
    setVendor_name(text);
  };
  const onSuggestionHandler = (text) => {
    setVendor_name(text);
    setSuggestions([]);
  };
  const resetFilters = () => {
    setVendor_name("");
    setSuggestions([]);
    setCreatedAfterDate(1);
    setCreatedBeforeDate(99999999999);
    setStatus("NEW");
    setStartDate(null);
    setEndDate(null);
    setFiltersReset(true);
  };

  return (
    <div className="prcContentWrap">
      {loading ? (
        <>
          <Container style={{ backgroundColor: "#EAEAEA" }}>
            <Row style={{ padding: "20px" }}>
              <h3 style={{ paddingBottom: "10px" }}>
                Filtriraj po statusu, datumu in podjetju
              </h3>
              <Col sm={2} md={2}>
                <select
                  value={status}
                  onChange={(e) => {
                    setStatus(e.target.value);
                  }}
                >
                  <option value="NEW">NEW</option>
                  <option value="APPROVED">APPROVED</option>
                  <option value="REJECTED">REJECTED</option>
                  <option value="DELETED">DELETED</option>
                </select>
              </Col>
              <Col>
                <InputGroup className="mb-3">
                  <Form.Control
                    className="col-md-12 input"
                    type="text"
                    onChange={(e) => onChangeHandler(e.target.value)}
                    value={vendor_name}
                  />
                  {vendor_name.length > 0 ? (
                    <InputGroup.Text
                      id="basic-addon2"
                      onClick={() => resetFilters()}
                    >
                      <FontAwesomeIcon icon={faX} />
                    </InputGroup.Text>
                  ) : (
                    <InputGroup.Text id="basic-addon2">
                      <FontAwesomeIcon icon={faMagnifyingGlass} />
                    </InputGroup.Text>
                  )}
                  {suggestions.length > 0 &&
                    suggestions.map((suggestion, i) => {
                      return (
                        <div
                          key={i}
                          className="suggestion col-md-12 justify-content-md-center"
                          onClick={() => onSuggestionHandler(suggestion.name)}
                        >
                          {suggestion.name}
                        </div>
                      );
                    })}
                </InputGroup>
              </Col>
              <Col>
                <DateRangePicker
                  startDatePlaceholderText={"Od"}
                  endDatePlaceholderText={"Do"}
                  startDateId="startDate"
                  endDateId="endDate"
                  startDate={startDate}
                  endDate={endDate}
                  showClearDates={true}
                  onDatesChange={({ startDate, endDate }) => {
                    setStartDate(startDate);
                    setEndDate(endDate);
                    setQueryDates(startDate, endDate);
                  }}
                  focusedInput={focusedInput}
                  onFocusChange={(focusedInput) =>
                    setFocusedInput(focusedInput)
                  }
                  isOutsideRange={() => false}
                  renderCalendarInfo={() => (
                    <MyDatePresets
                      startDate={startDate}
                      endDate={endDate}
                      displayFormat={"DD/MM/YYYY"}
                      handlePresets={(startDate, endDate) => {
                        setStartDate(startDate);
                        setEndDate(endDate);
                        setQueryDates(startDate, endDate);
                      }}
                    />
                  )}
                />
              </Col>
              <Col sm={2} md={2}>
                <Button variant="success" onClick={() => getAllReviews()}>
                  Osveži
                </Button>
              </Col>
            </Row>
          </Container>
          {selection.length > 0 && (
            <Container>
              <Row
                className="justify-content-md-center"
                style={{ margin: "20px" }}
              >
                <Col md={2}>
                  <Button variant="success" onClick={(e) => handleApprove(e)}>
                    Potrdi {selection.length} elementov
                  </Button>
                </Col>
                <Col>
                  <Button variant="danger" onClick={(e) => handleReject(e)}>
                    Zavrni {selection.length} elementov
                  </Button>
                </Col>
              </Row>
            </Container>
          )}
          {reviews.length > 0 ? (
            <Table striped bordered hover size="sm" {...getTableProps()}>
              <thead>
                {headerGroups.map((headerGroup) => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <th
                        {...column.getHeaderProps(
                          column.getSortByToggleProps()
                        )}
                      >
                        {column.render("Header")}
                        <span>
                          {column.isSorted
                            ? column.isSortedDesc
                              ? " 🔽"
                              : " 🔼"
                            : ""}
                        </span>
                        <div>
                          {column.canFilter ? column.render("Filter") : null}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {page.map((row, i) => {
                  prepareRow(row);
                  return (
                    <tr
                      {...row.getRowProps()}
                      onDoubleClick={() => handleRowClick(row)}
                    >
                      {row.cells.map((cell) => {
                        return (
                          <td {...cell.getCellProps()}>
                            {cell.render("Cell")}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          ) : (
            <div
              className="d-flex justify-content-center"
              style={{ position: "absolute", top: "50%", left: "50%" }}
            >
              <div className="align-content-stretch">Ni podatkov!</div>
            </div>
          )}

          <div className="pagination-bottom">
            <div className="Pagination">
              <div className="Pagination-previous">
                <Button
                  type="button"
                  onClick={() => previousPage()}
                  disabled={!canPreviousPage}
                  className="Pagination-btn"
                >
                  Previous
                </Button>
              </div>
              <div className="Pagination-center">
                <span className="Pagination-pageInfo">
                  Page{" "}
                  <div className="Pagination-pageJump">
                    <input
                      type="number"
                      value={pageIndex + 1}
                      onChange={(e) => {
                        const page = e.target.value
                          ? Number(e.target.value) - 1
                          : 0;
                        gotoPage(page);
                      }}
                    />
                  </div>{" "}
                  of{" "}
                  <span className="Pagination-totalPages">
                    {pageOptions.length}
                  </span>
                </span>
                <span className="select-wrap Pagination-pageSizeOptions">
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                    }}
                  >
                    {[10, 20, 30, 40, 50, 100].map((pageSize) => (
                      <option key={pageSize} value={pageSize}>
                        {pageSize} rows
                      </option>
                    ))}
                  </select>
                </span>
              </div>
              <div className="Pagination-next">
                <Button
                  type="button"
                  onClick={() => nextPage()}
                  disabled={!canNextPage}
                  className="Pagination-btn"
                >
                  Next
                </Button>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div
          className="d-flex justify-content-center"
          style={{ position: "absolute", top: "50%", left: "50%" }}
        >
          <div className="align-content-stretch">
            <Spinner animation="border" role="status" variant="primary" />
          </div>
        </div>
      )}
      <ToastContainer autoClose={10000} position="top-right" theme="light" />
    </div>
  );
};
